import { Cell } from '@/components/table/Cell';
import { ColumnSettingsDropdown } from '@/components/table/ColumnSettingsDropdown';
import { createCellKey } from '@/store/reducers/TableWidgetStore';
import { DragHandle } from '@/components/table/DragHandle';
import { DragType } from '@/components/table/Table';
import { ErrorCell } from '@/components/table/ErrorCell';
import { getAppConfig, optAppConfig } from '@/utils/config';
import { InlinePaperChipWidget } from '@/components/widgets/InlinePaperChipWidget';
import {
  mkDragTableColTrackAttrs,
  mkDragTableRowTrackAttrs,
} from '@/analytics/props/dragHandle';
import {
  mkHeaderAddColumnBtnTrackAttrs,
  mkHeaderSelectAllCheckTrackAttrs,
  mkRelatedRowSelectCheckTrackAttrs,
  mkRelatedSelectAllCheckTrackAttrs,
  mkRowSelectCheckTrackAttrs,
} from '@/analytics/props/litTable';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { RowSettingsDropdown } from '@/components/table/RowSettingsDropdown';
import { SIZE } from '@/components/widgets/InlineChipWidget';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import {
  tableCellFactory,
  TableCellRecord,
  TableColumn,
  TableRow,
} from '@/models/Table';

import {
  Box,
  Checkbox,
  css,
  IconButton,
  Link,
  ListItemButton,
  Skeleton,
  styled,
} from '@mui/material';
import {
  ChangeEvent,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import AddIcon from '@mui/icons-material/Add';
import classNames from 'classnames';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import invariant from 'invariant';
import MuiTable from '@mui/material/Table';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTableHead from '@mui/material/TableHead';
import MuiTableRow from '@mui/material/TableRow';

const PX_PER_COLUMN = 260;

export enum TableViewMode {
  LIT_REVIEW = 'LIT_REVIEW',
  RELATED_PAPERS = 'RELATED_PAPERS',
}

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

interface TableViewProps {
  rows: TableRow[];
  columns: TableColumn[];
  cells: TableCellRecord;
  numRowSelected: number;
  handleEditCell?: (cellKey: string, editedValue: string) => void;
  handleSelectAllRows: (event: ChangeEvent<HTMLInputElement>) => void;
  handleChangeRowSelection: (event: ChangeEvent<HTMLInputElement>) => void;
  hideAddPaperToTable?: never;
  handleClickAddColumn?: () => void;
  handleReorderTable?: (e: DragEndEvent) => void;
  mode?: TableViewMode;
  displayInlinePaperChip?: boolean;
}

const NOOP = (_: any) => {};
enum DragDirection {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
}

enum StickyClassName {
  LEFT = 'sticky-left',
  TOP = 'sticky-top',
}
const stickyClassName = (left?: boolean, top?: boolean) =>
  [left && StickyClassName.LEFT, top && !left && StickyClassName.TOP]
    .filter(Boolean)
    .join(' ');

export const TableView = ({
  rows,
  columns,
  cells,
  numRowSelected,
  handleSelectAllRows,
  handleChangeRowSelection,
  handleClickAddColumn,
  handleEditCell,
  handleReorderTable = NOOP,
  mode = TableViewMode.LIT_REVIEW,
  displayInlinePaperChip = true, // I removed the functionality, but hid it behind a prop
}: TableViewProps): ReactNode => {
  const { isReadOnly } = useContext(PermissionsContext);
  const tableViewport = useRef<HTMLDivElement | null>(null);
  const papersColumn = useRef<HTMLElement | null>(null);
  const [isStickyLeft, setStickyLeft] = useState(false);
  const [isTopSticky, setTopSticky] = useState(false);
  const isDndEnabled = getAppConfig((_) => _.table.is_dnd_enabled, false);

  const [activeDragItem, setActiveDragItem] = useState<{
    itemId?: string;
    dragType: DragType;
    displayTitle?: string;
  } | null>(null);
  const [dragOverItem, setDragOverItem] = useState<{
    itemId?: string | null;
    directionVertical: DragDirection;
    directionHorizontal: DragDirection;
  } | null>(null);

  const handleStartDragItem = (e: DragStartEvent): void => {
    setActiveDragItem({
      itemId: e.active?.id?.toString(),
      dragType: e.active?.data?.current?.type,
      displayTitle: e.active?.data?.current?.displayTitle,
    });
  };

  const handleDragOverItem = (e: DragOverEvent): void => {
    setDragOverItem({
      itemId: e.over?.id?.toString(),
      directionVertical: e.delta?.y > 0 ? DragDirection.DOWN : DragDirection.UP,
      directionHorizontal:
        e.delta?.x > 0 ? DragDirection.RIGHT : DragDirection.LEFT,
    });
  };

  const handleDropItem = (e: DragEndEvent): void => {
    setDragOverItem(null);
    handleReorderTable(e);
  };

  const isRelatedPapersMode = mode === TableViewMode.RELATED_PAPERS;

  if (isRelatedPapersMode) {
    invariant(
      !handleClickAddColumn,
      'handleClickAddColumn cannot be used for TableView in RELATED_PAPERS mode',
    );
  }

  useEffect(() => {
    let observer: IntersectionObserver;
    if (papersColumn.current && tableViewport.current) {
      observer = new IntersectionObserver(
        ([e]) => {
          if (e.rootBounds?.left != null) {
            setStickyLeft(e.rootBounds.left > e.boundingClientRect.left + 1);
          }
          if (e.rootBounds?.top != null) {
            setTopSticky(e.rootBounds.top > e.boundingClientRect.top);
          }
        },
        {
          root: tableViewport.current,
          rootMargin: '-10px',
          threshold: [...Array(100).keys()].map((x) => x / 100),
        },
      );

      observer.observe(papersColumn.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, [papersColumn.current, tableViewport.current]);

  return (
    <DndContext
      onDragEnd={handleDropItem}
      onDragOver={handleDragOverItem}
      onDragStart={handleStartDragItem}
    >
      <MuiTableContainer
        ref={tableViewport}
        sx={{
          overflow: 'visible',
          height: 'auto',
          maxWidth: '100%',
        }}
      >
        <MuiTable
          stickyHeader
          sx={{
            minWidth: '100%',
            padding: (theme) => theme.spacing(3),
          }}
          size="small"
        >
          <MuiTableHead>
            <MuiTableRow>
              {!isReadOnly && (
                <StyledTableHeadingCell width="1px">
                  {noraExperienceVariant !== TABLE_ANNOTATION && (
                    <CheckboxContainer>
                      <Checkbox
                        checked={numRowSelected === rows.length}
                        color="default"
                        size="small"
                        indeterminate={
                          numRowSelected > 0 && numRowSelected < rows.length
                        }
                        onChange={handleSelectAllRows}
                        {...(mode === TableViewMode.LIT_REVIEW
                          ? mkHeaderSelectAllCheckTrackAttrs({
                              selectedRowCount: numRowSelected,
                            })
                          : mkRelatedSelectAllCheckTrackAttrs({
                              selectedRowCount: numRowSelected,
                            }))}
                        sx={(theme) => ({
                          verticalAlign: 'middle',
                          padding: theme.spacing(0.75),
                          color: 'rgba(250, 242, 243, 0.8)', // cream-80
                        })}
                      />
                    </CheckboxContainer>
                  )}
                </StyledTableHeadingCell>
              )}
              <PaperNameHeadingCell
                ref={papersColumn}
                className={stickyClassName(isStickyLeft, isTopSticky)}
              >
                Paper
              </PaperNameHeadingCell>
              {columns.map((column: TableColumn, index) => {
                const isDraggingOverCol =
                  activeDragItem?.dragType === DragType.COLUMN &&
                  dragOverItem?.itemId === column.id;
                const isDraggingLeft =
                  dragOverItem?.directionHorizontal === DragDirection.LEFT;

                return (
                  <StyledTableHeadingCell
                    key={column.id}
                    sx={{
                      minWidth: PX_PER_COLUMN + 'px',
                    }}
                    className={classNames(
                      {
                        'col__drop-target--left':
                          isDraggingOverCol && isDraggingLeft,
                        'col__drop-target--right':
                          isDraggingOverCol && !isDraggingLeft,
                      },
                      stickyClassName(false, isTopSticky),
                    )}
                  >
                    <TableRowHeaderContents>
                      <DragHandle
                        dragType={DragType.COLUMN}
                        dragId={column.id}
                        index={index}
                        metadata={{
                          displayTitle: column.name,
                        }}
                        {...mkDragTableColTrackAttrs({
                          index,
                          columnName: column.name,
                        })}
                      >
                        {column.name}
                      </DragHandle>
                      {mode === TableViewMode.LIT_REVIEW && (
                        <ColumnSettingsDropdown column={column} mode={mode} />
                      )}
                    </TableRowHeaderContents>
                  </StyledTableHeadingCell>
                );
              })}
              {!isReadOnly && (
                <StyledTableHeadingCell
                  sx={{ paddingRight: 0, width: '1px', borderRight: 0 }}
                  className={stickyClassName(false, isTopSticky)}
                >
                  {!!handleClickAddColumn && (
                    <StyledActionsEndContainer>
                      <AddColumnButton
                        onClick={handleClickAddColumn}
                        {...mkHeaderAddColumnBtnTrackAttrs()}
                      >
                        <AddIcon fontSize="small" />
                      </AddColumnButton>
                    </StyledActionsEndContainer>
                  )}
                </StyledTableHeadingCell>
              )}
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {rows.map((row: TableRow, index: number) => {
              const isDraggingOverRow =
                activeDragItem?.dragType === DragType.ROW &&
                dragOverItem?.itemId === row.id;
              const isDraggingUp =
                dragOverItem?.directionVertical === DragDirection.UP;

              return (
                <StyledTableRow
                  key={row.id}
                  className={classNames({
                    'row__drop-target--above':
                      isDraggingOverRow && isDraggingUp,
                    'row__drop-target--below':
                      isDraggingOverRow && !isDraggingUp,
                  })}
                >
                  {!isReadOnly && (
                    <TableCell>
                      <StyledTableCellActionsContainer>
                        <Box display="flex" alignItems="center">
                          {isDndEnabled && (
                            <ShowOnRowHover className="hidden-div">
                              <DragHandle
                                dragType={DragType.ROW}
                                dragId={row.id}
                                index={index}
                                metadata={{ displayTitle: row.displayValue }}
                                {...mkDragTableRowTrackAttrs({
                                  index,
                                  paperCorpusId: row.paperCorpusId,
                                  visibleRows: rows.length,
                                })}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </DragHandle>
                            </ShowOnRowHover>
                          )}
                          {noraExperienceVariant !== TABLE_ANNOTATION && (
                            <Checkbox
                              value={row.id}
                              onChange={handleChangeRowSelection}
                              color="default"
                              size="small"
                              sx={(theme) => ({
                                color: 'rgba(250, 242, 243, 0.8)', // cream-80
                                padding: theme.spacing(0.75),
                              })}
                              {...(mode === TableViewMode.LIT_REVIEW
                                ? mkRowSelectCheckTrackAttrs({
                                    rowId: row.id,
                                    index,
                                  })
                                : mkRelatedRowSelectCheckTrackAttrs({
                                    rowId: row.id,
                                    index,
                                  }))}
                            />
                          )}
                        </Box>
                      </StyledTableCellActionsContainer>
                    </TableCell>
                  )}
                  <PaperColumnCell
                    sx={{ verticalAlign: 'top' }}
                    className={stickyClassName(isStickyLeft)}
                  >
                    <PaperContentsContainer>
                      <PaperNameContents
                        displayInlinePaperChip={displayInlinePaperChip}
                        displayValue={row.displayValue}
                        paperCorpusId={row.paperCorpusId}
                        isRelatedPapersMode={isRelatedPapersMode}
                        mode={mode}
                      />
                    </PaperContentsContainer>
                  </PaperColumnCell>
                  {columns.map((column: TableColumn) => {
                    const isDraggingOverCol =
                      activeDragItem?.dragType === DragType.COLUMN &&
                      dragOverItem?.itemId === column.id;
                    const isDraggingLeft =
                      dragOverItem?.directionHorizontal === DragDirection.LEFT;

                    const cellKey = createCellKey(row.id, column.id);
                    const cell =
                      cells[cellKey] || tableCellFactory({ isLoading: true });
                    return (
                      <TableCell
                        key={`${column.id}--${row.id}`}
                        sx={{ verticalAlign: 'top' }}
                        className={classNames({
                          'col__drop-target--left':
                            isDraggingOverCol && isDraggingLeft,
                          'col__drop-target--right':
                            isDraggingOverCol && !isDraggingLeft,
                        })}
                      >
                        {cell.isLoading ? (
                          <StyledSkeleton />
                        ) : cell.error ? (
                          <ErrorCell row={row} column={column} />
                        ) : (
                          <Cell
                            cell={cell}
                            cellKey={cellKey}
                            onEditCell={handleEditCell}
                            isReadOnly={isReadOnly}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                  {!isReadOnly && (
                    <StyledTableCellActionsEnd>
                      <StyledActionsEndContainer>
                        <ShowOnRowHover className="hidden-div">
                          <RowSettingsDropdown row={row} mode={mode} />
                        </ShowOnRowHover>
                      </StyledActionsEndContainer>
                    </StyledTableCellActionsEnd>
                  )}
                </StyledTableRow>
              );
            })}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>

      <Fragment>
        {isDndEnabled && activeDragItem && (
          <DragOverlay>
            <PreviewDraggedRow>{activeDragItem.displayTitle}</PreviewDraggedRow>
          </DragOverlay>
        )}
      </Fragment>
    </DndContext>
  );
};

type PaperNameContentsProps = {
  displayInlinePaperChip: boolean;
  displayValue?: string;
  paperCorpusId?: number;
  isRelatedPapersMode: boolean;
  mode: TableViewMode;
};

const PaperNameContents = ({
  displayInlinePaperChip,
  displayValue,
  paperCorpusId,
  isRelatedPapersMode,
  mode,
}: PaperNameContentsProps) => {
  if (!displayInlinePaperChip) {
    return (
      <PaperContentsContainer>
        {paperCorpusId ? (
          <SecondaryColorLink
            href={`https://semanticscholar.org/p/${paperCorpusId}`}
            target="_blank"
            rel="noopener noreferer"
          >
            {displayValue}
          </SecondaryColorLink>
        ) : (
          displayValue
        )}
      </PaperContentsContainer>
    );
  }
  return (
    paperCorpusId &&
    (isRelatedPapersMode ? (
      <PermissionsContext.Provider value={{ isReadOnly: false }}>
        <InlinePaperChipWidget
          corpusId={paperCorpusId}
          paperTitle={displayValue ?? `corpusId: ${paperCorpusId}`}
          mode={mode}
          isMultiLine
          warnIfNotSearchable
          isDarkMode
          hasMargin
          size={SIZE.small}
          icon={false}
        />
      </PermissionsContext.Provider>
    ) : (
      <InlinePaperChipWidget
        corpusId={paperCorpusId}
        paperTitle={displayValue ?? `corpusId: ${paperCorpusId}`}
        isMultiLine
        warnIfNotSearchable
        hideAddPaperToTable={mode === TableViewMode.LIT_REVIEW}
        isDarkMode
        hasMargin
        size={SIZE.small}
        icon={false}
      />
    ))
  );
};

export const TableCell = styled(MuiTableCell)`
  background-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  color: inherit;
  height: 1px; // force height
  padding: 0px; // no default padding -- overriden if needed
  position: relative;
  border-right: 1px solid rgba(250, 242, 243, 0.1); // cream-10
  border-bottom: 1px solid rgba(250, 242, 243, 0.1); // cream-10
  &.${StickyClassName.LEFT} {
    clip-path: inset(0px -15px 0px 0px);
    filter: drop-shadow(rgb(3, 38, 41) 4px 0px 4px); // extra-dark-teal-100
  }
  &.${StickyClassName.TOP} {
    clip-path: inset(0px 0px -15px 0px);
    filter: drop-shadow(rgb(3, 38, 41) 0px 4px 4px); // extra-dark-teal-100
  }

  ::after {
    content: '';
    width: 2px;
    top: 0;
    bottom: 0;
    position: absolute;
    z-index: 5;
  }

  &.col__drop-target--left {
    ::after {
      background-color: ${({ theme }) => theme.color['pink-100'].hex};
      left: 0;
      z-index: 5;
    }
  }

  &.col__drop-target--right {
    ::after {
      background-color: ${({ theme }) => theme.color['pink-100'].hex};
      right: 0;
      z-index: 5;
    }
  }
`;

const StyledTableHeadingCell = styled(TableCell)`
  font-weight: 700;
  top: 0;
  position: sticky;
  padding-top: 0;
  z-index: 1;
  padding: 0;
  border-bottom: 2px solid rgba(250, 242, 243, 0.1); // cream-10
`;

const stickyLeft = css`
  position: sticky;
  left: 0;
`;

const PaperNameHeadingCell = styled(StyledTableHeadingCell)`
  ${stickyLeft}
  min-width: ${PX_PER_COLUMN}px;
  z-index: 3;
  padding: ${({ theme }) => theme.spacing(0.75)};
`;

const PaperColumnCell = styled(TableCell)`
  ${stickyLeft}
  z-index: 2;
`;

const StyledTableCellActionsEnd = styled(TableCell)`
  border-right: 0;
`;

const CheckboxContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  height: 100%;
`;

const StyledTableCellActionsContainer = styled(CheckboxContainer)`
  align-items: start;
  padding-top: ${({ theme }) => theme.spacing(0.25)};
`;

const StyledActionsEndContainer = styled(Box)`
  display: flex;
  background-color: inherit;
  justify-content: right;
  height: 100%;
  padding-right: 0;
`;

const PaperContentsContainer = styled(Box)`
  height: 100%;
  min-width: ${PX_PER_COLUMN}px;
  padding: ${({ theme }) => theme.spacing(0.75)};
`;

const SecondaryColorLink = styled(Link)`
  color: ${({ theme }) => theme.palette.secondary.main};
  display: block;
  &:hover {
    color: currentColor;
  }
`;

const ShowOnRowHover = styled('div')`
  opacity: 0;
  transition: opacity 400ms ease-in-out;
`;

const showHiddenDivOnHover = css`
  &:hover .hidden-div {
    opacity: 1;
    transition-duration: 150ms;
  }
`;

export const TableRowHeaderContents = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing(0.75)};
`;

export const StyledTableRow = styled(MuiTableRow)`
  ${showHiddenDivOnHover}
  position: relative;

  transition: color 400ms ease-in-out;
  &:hover td > div {
    background-color: rgba(250, 242, 243, 0.1); // cream-10
    transition-duration: 150ms;
  }

  ::after {
    content: '';
    height: 2px;
    left: 0;
    right: 0;
    position: absolute;
    z-index: 5;
  }

  &.row__drop-target--above {
    ::after {
      background-color: ${({ theme }) => theme.color['pink-100'].hex};
      top: 0;
      z-index: 5;
    }
  }

  &.row__drop-target--below {
    ::after {
      background-color: ${({ theme }) => theme.color['pink-100'].hex};
      bottom: 0;
      z-index: 5;
    }
  }
`;

// used in row and column dropdowns
export const PopoverListButton = styled(ListItemButton)`
  font-weight: ${({ theme }) => theme.font.weight.bold};

  & .MuiSvgIcon-root {
    margin-right: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const StyledSkeleton = styled(Skeleton)`
  font-size: 1rem;
  width: 90%;
  background-color: rgb(3, 38, 41); // extra-dark-teal-100
`;

export const AddColumnButton = styled(IconButton)`
  border-radius: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.color['green-100'].hex};
  padding: ${({ theme }) => theme.spacing(0.5)};
`;

const PreviewDraggedRow = styled('div')`
  background-color: rgb(3, 38, 41); // extra-dark-teal-100
  border-radius: 4px;
  color: ${({ theme }) => theme.color['cream-100'].hex};
  cursor: grabbing;
  min-width: 250px;
  padding: 4px;
`;
